import { useState, useEffect, createContext } from "react";
import { CompanySettingsProvider } from "../contexts/CompanySettingsContext.jsx";
import { DateSettingsProvider } from "../contexts/DateSettingsContext.jsx";
import { TimeSeriesDataProvider } from "../contexts/TimeSeriesDataContext.jsx";
import LoadingVisualization from "./LoadingVisualization.jsx";

const UserContext = createContext();

export { UserContext };

export default function Auth(props) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [originalUser, setOriginalUser] = useState(null); // Track original user when impersonating

  useEffect(() => {
    // Attempt to fetch the logged-in user info
    async function checkExistingSession() {
      // Skip authentication check for public routes
      const publicRoutes = ["/demo/", "/login"];
      const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));
      
      if (isPublicRoute) {
        return setLoading(false);
      }

      try {
        // "credentials: 'include'" ensures cookies are sent
        const response = await fetch("/auth/login", { credentials: "include" });
        if (!response.ok) {
          // If the server returns 404 or other non-2xx, throw to skip setting user
          throw new Error("No existing session");
        }
        const data = await response.json();
        data.settings = JSON.parse(data.settings);
        setUser(data);

        // If this user has admin privileges, they might be the original user
        // We'll track this for impersonation purposes
        if (data.admin && !originalUser) {
          setOriginalUser(data);
        }
      } catch (err) {
        // If we get an error (e.g. 404), the user wasnt logged in
        console.log(err.message);
      } finally {
        setLoading(false);
      }
    }

    checkExistingSession();
  }, []);

  // Helper function to start impersonation
  const startImpersonation = (impersonatedUser) => {
    // Store the current user as original if not already set
    if (!originalUser && user?.admin) {
      setOriginalUser(user);
    }
    setUser(impersonatedUser);
  };

  // Helper function to stop impersonation
  const stopImpersonation = async () => {
    try {
      const response = await fetch('/auth/stop_impersonation', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        setUser(result.returned_to_user);
        setOriginalUser(null);
        return true;
      }
    } catch (err) {
      console.error('Error stopping impersonation:', err);
    }
    return false;
  };

  // Check if currently impersonating
  const isImpersonating = originalUser && user && originalUser.id !== user.id;

  // Logout function - redirects to backend logout endpoint
  const logout = () => {
    // Let the backend handle clearing the session and state
    // Don't clear local state here to avoid race condition
    
    // Redirect to backend logout endpoint which will clear session cookies
    // and redirect back to the root page
    window.location.href = '/auth/logout';
  };

  // While we're still checking the server, render a centered loading spinner
  if (loading) {
    return <LoadingVisualization />;
  }

  // Always provide the UserContext, let individual routes handle authentication
  return (
    <UserContext.Provider value={{ 
      user, 
      setUser, 
      originalUser,
      isImpersonating,
      startImpersonation,
      stopImpersonation,
      logout
    }}>
      <CompanySettingsProvider>
        <DateSettingsProvider>
          <TimeSeriesDataProvider>
            {props.children}
          </TimeSeriesDataProvider>
        </DateSettingsProvider>
      </CompanySettingsProvider>
    </UserContext.Provider>
  );
}
